/* ---------------------------------------------------------------------------
 * Pair With — the fixed window, and the top-up after an add
 *
 * The panel renders its whole pool up front and hides everything past the
 * window (`data-ryx-pdp-pair-with-pool`). When one of the visible cards is
 * added to the cart the card is retired and the next pooled card takes its
 * slot, so the window stays the same size and the panel keeps a fixed height.
 *
 * Two add paths reach the same card, and they do not look alike:
 *
 *   1. Single-variant products add straight from the card. Horizon dispatches
 *      `CartLinesUpdateEvent` on the `product-form-component` inside the card,
 *      so the card is `event.target.closest(PANEL)`.
 *   2. Every other product opens Horizon's shared quick-add modal, which lives
 *      once in `layout/theme.liquid`. The form that adds is the modal's, and
 *      nothing in that subtree points back at the card that opened it — so the
 *      card is remembered from the click that opened the modal and matched
 *      against the product id the promise resolves with.
 *
 * The product id, not the element, is what identifies the card: Horizon reports
 * `detail.productId` from the form's own `data-product-id`, which the quick-add
 * snippet sets from the product it was rendered for.
 *
 * This is its own asset rather than part of `ryoxzel-pdp.js` for the same
 * reason `ryoxzel-quantity-tiers.js` is: the block is one concern and the file
 * that owns it should be readable on its own. It is loaded under the same
 * `ryoxzel_pdp_suffixes` gate, because the block only exists on those templates.
 * ------------------------------------------------------------------------- */

import { StandardEvents } from '@shopify/events';

const PANEL = '[data-ryx-pdp-pair-with]';
const LIST = '[data-ryx-pdp-pair-with-list]';
const ITEM = '[data-ryx-pdp-pair-with-item]';
const PRODUCT_ATTR = 'data-ryx-pdp-pair-with-product-id';
const POOL_ATTR = 'data-ryx-pdp-pair-with-pool';
const LEAVING_ATTR = 'data-ryx-pdp-pair-with-leaving';
const EMPTY_ATTR = 'data-ryx-pdp-empty';
const QUICK_ADD_DIALOG = '#quick-add-dialog';

/* Long enough to read as a fade, short enough that a second add cannot land on
   a card that is still in the DOM. */
const LEAVE_MS = 180;

/* A modal left open for longer than this is not the add we are looking at. */
const PENDING_TTL_MS = 120000;

/** @type {Element | null} The card whose quick-add button opened the modal. */
let pendingCard = null;
let pendingAt = 0;

function panelOf(element) {
  return element instanceof Element ? element.closest(PANEL) : null;
}

function cardForProduct(panel, productId) {
  const list = panel?.querySelector(LIST);
  if (!list) return null;

  return Array.from(list.querySelectorAll(ITEM)).find(
    (card) => card.getAttribute(PRODUCT_ATTR) === productId
  ) || null;
}

/* Remember which card opened the modal. Capture, so this runs before Horizon's
   own handler decides whether the click adds or opens the dialog. */
document.addEventListener(
  'click',
  (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target || !target.closest('button') || !target.closest('quick-add-component')) return;

    const panel = panelOf(target);
    if (!panel) return;

    pendingCard = panel;
    pendingAt = Date.now();
  },
  true
);

/* One dialog serves every product, so the card that opened it stops being
   relevant the moment it closes.
 *
 * Horizon's own `dialog:close` is a non-bubbling CustomEvent dispatched on the
 * dialog component, so it never reaches document. The element's native `close`
 * does not bubble either, but the capture phase still walks down to the target,
 * which is why this listens in capture — the same trick `assets/dialog.js`
 * already uses for `toggle`. */
document.addEventListener(
  'close',
  (event) => {
    if (!(event.target instanceof HTMLDialogElement)) return;
    if (!event.target.closest(QUICK_ADD_DIALOG)) return;
    pendingCard = null;
  },
  true
);

function retire(panel, productId) {
  const card = cardForProduct(panel, productId);
  if (!card) return;

  card.setAttribute(LEAVING_ATTR, '');

  window.setTimeout(() => {
    card.remove();

    const list = panel.querySelector(LIST);
    const next = list?.querySelector(`[${POOL_ATTR}]`);

    if (next) {
      next.removeAttribute(POOL_ATTR);
      next.hidden = false;
    }

    /* An empty panel has to say so: the tabs controller drops a panel whose
       text is empty, and with the last card gone there is no text left. */
    if (list && !list.querySelector(ITEM)) {
      panel.setAttribute(EMPTY_ATTR, 'true');
      panel.hidden = true;
    }
  }, LEAVE_MS);
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (event.action !== 'add') return;
  if (!(event.target instanceof Element)) return;

  const form = event.target.closest('product-form-component');
  const addedId = form?.dataset.productId;
  if (!addedId) return;

  /* The card that opened the modal is only the right card while the modal was
     opened from a card at all, and while the id it reports is the card's. */
  const fromModal =
    pendingCard && Date.now() - pendingAt < PENDING_TTL_MS ? cardForProduct(pendingCard, addedId) : null;

  const panel = panelOf(event.target) || (fromModal ? pendingCard : null);
  if (!panel) return;

  event.promise
    ?.then(({ detail }) => {
      if (detail?.didError) return;
      retire(panel, addedId);
    })
    .catch(() => {
      /* Horizon's product form owns presentation of real cart errors. */
    });
});
